import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Info } from 'lucide-react';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { RevenueSource } from '../../types/experiment.types';

/** What the tag needs: a full `RevenueSource`, or a per-row source (arm comparison). */
export type RevenueSourceLabel = Pick<RevenueSource, 'source_label' | 'description'> & {
  is_fallback?: boolean;
};

interface Props {
  /** Absent / null (older builds, experiments with no data yet) renders nothing. */
  source: RevenueSourceLabel | null | undefined;
  className?: string;
}

/**
 * Small "GA4" / "Google Ads" tag beside a revenue figure or its section header.
 * The backend's ready-made `description` is the tooltip, and a fallback source
 * (GA4 / Adobe had no revenue) carries an info glyph so it reads as a caveat.
 */
export function RevenueSourceTag({ source, className }: Props) {
  if (!source) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          aria-label={source.description}
          className={cn(
            'bg-muted text-muted-foreground hover:bg-muted hover:text-foreground h-auto cursor-help gap-1 rounded-sm px-1.5 py-0.5 align-middle text-[11px] leading-none font-medium whitespace-nowrap normal-case',
            className,
          )}
        >
          {source.source_label}
          {source.is_fallback && <Info className="h-3 w-3" aria-hidden="true" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">
        {source.description}
        <TooltipPrimitive.Arrow className="fill-popover" />
      </TooltipContent>
    </Tooltip>
  );
}
